import { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { Volume2, VolumeX, RotateCcw, Sparkles } from "lucide-react"
import type { ProfileResponse } from "~/api/auth"
import { HeroPortraitFrame } from "~/components/HeroPortraitFrame"
import { heroPortraitUrl } from "~/utils/heroPortraitUrl"

interface PlusWelcomeCelebrationProps {
  profile: ProfileResponse
  onDismiss: () => void
}

const PLUS_BENEFITS = [
  "Unlimited Ask Dynamo",
  "Weekly Perspectives & Ideas",
  "Your dog’s Story",
  "Build your pack",
] as const

const SONIC_SRC = "/audio/doggy-dynamo-sonic-logo.mp3"
const SONIC_PLAYED_KEY = "doggy_dynamo.plus_welcome_sonic_played"

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

function createSonicAudio(muted: boolean): HTMLAudioElement {
  const audio = new Audio(SONIC_SRC)
  audio.loop = false
  audio.muted = muted
  audio.setAttribute("playsinline", "true")
  // iOS Safari: playsInline as a property when available
  ;(audio as HTMLAudioElement & { playsInline?: boolean }).playsInline = true
  return audio
}

export function PlusWelcomeCelebration({ profile, onDismiss }: PlusWelcomeCelebrationProps) {
  const dog = profile.dog_profile
  const dogName = dog?.name ?? "Your dog"
  const archetypeLabel = dog?.archetype_label
  const heroImage = heroPortraitUrl(dog?.archetype_icon, dog?.portrait)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const gestureUnlockRef = useRef(false)
  const [muted, setMuted] = useState(false)
  const [showConfetti, setShowConfetti] = useState(() => !prefersReducedMotion())
  const [confettiKey, setConfettiKey] = useState(0)
  const [cardReady, setCardReady] = useState(false)

  useEffect(() => {
    const id = window.requestAnimationFrame(() => setCardReady(true))
    return () => window.cancelAnimationFrame(id)
  }, [])

  useEffect(() => {
    if (!cardReady || prefersReducedMotion()) return
    if (sessionStorage.getItem(SONIC_PLAYED_KEY) === "1") return

    const audio = createSonicAudio(false)
    audioRef.current = audio

    const onPlaying = () => {
      sessionStorage.setItem(SONIC_PLAYED_KEY, "1")
      gestureUnlockRef.current = false
    }
    audio.addEventListener("playing", onPlaying)

    const play = audio.play()
    if (play && typeof play.then === "function") {
      play.catch((err: unknown) => {
        const name = err instanceof Error ? err.name : ""
        if (name === "NotAllowedError" || name === "NotSupportedError") {
          gestureUnlockRef.current = true
        }
      })
    }

    return () => {
      audio.removeEventListener("playing", onPlaying)
      audio.pause()
      if (audioRef.current === audio) audioRef.current = null
    }
  }, [cardReady])

  useEffect(() => {
    if (!showConfetti) return
    const timer = window.setTimeout(() => setShowConfetti(false), 2800)
    return () => window.clearTimeout(timer)
  }, [showConfetti, confettiKey])

  function toggleMute() {
    const audio = audioRef.current
    const next = !muted
    setMuted(next)
    if (audio) audio.muted = next
  }

  function playSonic({ restartConfetti }: { restartConfetti: boolean }) {
    if (prefersReducedMotion() && !restartConfetti) return

    const previous = audioRef.current
    previous?.pause()
    const audio = createSonicAudio(muted)
    audioRef.current = audio
    void audio.play()?.catch(() => undefined)

    if (restartConfetti && !prefersReducedMotion()) {
      setConfettiKey((key) => key + 1)
      setShowConfetti(true)
    }
  }

  function replaySonic() {
    playSonic({ restartConfetti: true })
  }

  function handleOverlayPointerDown() {
    if (!gestureUnlockRef.current) return
    gestureUnlockRef.current = false
    const audio = audioRef.current
    if (audio) {
      const onPlaying = () => {
        sessionStorage.setItem(SONIC_PLAYED_KEY, "1")
        audio.removeEventListener("playing", onPlaying)
      }
      audio.addEventListener("playing", onPlaying)
      void audio.play()?.catch(() => undefined)
      return
    }
    playSonic({ restartConfetti: false })
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-3 pb-[calc(3.5rem+env(safe-area-inset-bottom,0px))] sm:p-4 sm:pb-4"
      role="presentation"
      data-testid="plus-welcome-overlay"
      onPointerDown={handleOverlayPointerDown}
    >
      {showConfetti && (
        <div key={confettiKey} className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          {Array.from({ length: 18 }).map((_, index) => (
            <span
              key={index}
              className="plus-welcome-confetti"
              style={{
                left: `${6 + ((index * 17) % 88)}%`,
                animationDelay: `${(index % 6) * 0.12}s`,
                backgroundColor: index % 3 === 0 ? "#c47a3a" : index % 3 === 1 ? "#5b7c5a" : "#e8c27a",
              }}
            />
          ))}
        </div>
      )}

      <div
        className="relative flex max-h-[min(90svh,calc(100svh-3.5rem-env(safe-area-inset-bottom,0px)-1.5rem))] w-full max-w-lg flex-col overflow-y-auto rounded-2xl bg-[var(--color-surface-card)] p-4 shadow-xl sm:p-5"
        role="dialog"
        aria-labelledby="plus-welcome-heading"
        aria-modal="true"
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-2">
          <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-ask-dynamo)]" aria-hidden />
          <div className="min-w-0 flex-1">
            <h2
              id="plus-welcome-heading"
              className="mt-0.5 text-lg font-semibold text-[var(--color-text-primary)] sm:text-xl"
            >
              Welcome to Doggy Dynamo Plus
            </h2>
            <p className="mt-0.5 text-sm text-[var(--color-text-secondary)]">
              A richer way to understand and remember your dog.
            </p>
          </div>
          <div className="flex shrink-0 gap-1">
            <button
              type="button"
              onClick={toggleMute}
              aria-label={muted ? "Unmute Sonic logo" : "Mute Sonic logo"}
              className="rounded-lg border border-[var(--color-divider)] p-2 text-[var(--color-text-secondary)]"
            >
              {muted ? <VolumeX size={16} aria-hidden /> : <Volume2 size={16} aria-hidden />}
            </button>
            <button
              type="button"
              onClick={replaySonic}
              aria-label="Replay Sonic logo"
              className="rounded-lg border border-[var(--color-divider)] p-2 text-[var(--color-text-secondary)]"
            >
              <RotateCcw size={16} aria-hidden />
            </button>
          </div>
        </div>

        {heroImage ? (
          <div className="mt-3 max-h-36 overflow-hidden sm:mt-4 sm:max-h-none">
            <HeroPortraitFrame
              src={heroImage}
              alt={`Portrait for ${dogName}`}
            />
          </div>
        ) : (
          <div className="mt-3 flex h-28 items-center justify-center rounded-xl border border-dashed border-[var(--color-divider)] bg-[var(--color-bg-primary)] text-sm text-[var(--color-text-secondary)] sm:mt-4 sm:h-40">
            {dogName}
            {archetypeLabel ? ` · ${archetypeLabel}` : ""}
          </div>
        )}

        <p className="mt-3 text-sm font-medium text-[var(--color-text-primary)] sm:mt-4 sm:text-base">
          {dogName}
          {archetypeLabel ? ` · ${archetypeLabel}` : ""}
        </p>

        <ul className="mt-2 space-y-1.5 text-sm text-[var(--color-text-secondary)] sm:mt-3 sm:space-y-2">
          {PLUS_BENEFITS.map((benefit) => (
            <li key={benefit} className="flex gap-2">
              <span aria-hidden className="text-[var(--color-ask-dynamo)]">
                ✓
              </span>
              <span>{benefit}</span>
            </li>
          ))}
        </ul>

        <div className="mt-4 flex flex-col gap-2 sm:mt-5">
          <Link
            to="/plus"
            onClick={onDismiss}
            className="inline-flex w-full items-center justify-center rounded-lg bg-[var(--color-ask-dynamo)] px-4 py-2.5 text-sm font-medium text-white"
          >
            Explore Plus
          </Link>
          <button
            type="button"
            onClick={onDismiss}
            className="inline-flex w-full items-center justify-center rounded-lg border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-4 py-2.5 text-sm font-medium text-[var(--color-text-primary)]"
          >
            Go to Home
          </button>
        </div>
      </div>
    </div>
  )
}
